import { HTMLElementUtils, StaticDependenciesService } from '@cinetixx/cinetixx-ui';

interface IButtonPoint {
  x: number;
  y: number;
}

type ElementButtonSide = 'left' | 'right' | 'top' | 'bottom';

export class ElementButtonService {

  private readonly _container: HTMLElement;
  private readonly _options: {
    buttonInnerHTML?: string;
    additionalButtonClassName?: string;
    elementsInsideContainerSharedClassName: string;
    squareWidthButtons?: boolean;
  };
  private _data: any;

  public constructor(
    container: HTMLElement,
    options: {
      elementsInsideContainerSharedClassName: string,
      buttonInnerHTML?: string;
      additionalButtonClassName?: string;
      squareWidthButtons?: boolean;
    }
  ) {
    this._container = container;
    this._options = options;
  }

  public addButtonsToElement(
    el: HTMLElement,
    direction: 'horizontal' | 'vertical',
    data?: any,
    sides?: ElementButtonSide[]
  ): void {
    this.removeButtons(el);
    this._data = data ?? null;

    const resolvedSides = sides?.length
      ? sides
      : direction === 'horizontal'
        ? (['left', 'right'] as ElementButtonSide[])
        : (['top', 'bottom'] as ElementButtonSide[]);

    for (const side of resolvedSides) {
      this.createAndAppendButton(el, side);
    }
  }

  private createAndAppendButton(el: HTMLElement, className: ElementButtonSide): void {
    const button = this.getElementButton(el, className);
    button.classList.add(className);
    el.appendChild(button);

    const rotation = HTMLElementUtils.getRotationDegrees(el.querySelector('.shape'));
    const btnRotateRect = HTMLElementUtils.getRotatedRect(button, -rotation);

    const topLeft: IButtonPoint = this.rotatePoint(
      { x: btnRotateRect.left - this._container.offsetLeft, y: btnRotateRect.top - this._container.offsetTop },
      rotation,
      btnRotateRect
    );
    const topRight: IButtonPoint = this.rotatePoint(
      { x: btnRotateRect.right - this._container.offsetLeft, y: btnRotateRect.top - this._container.offsetTop },
      rotation,
      btnRotateRect
    );
    const bottomRight: IButtonPoint = this.rotatePoint(
      { x: btnRotateRect.right - this._container.offsetLeft, y: btnRotateRect.bottom - this._container.offsetTop },
      rotation,
      btnRotateRect
    );
    const bottomLeft: IButtonPoint = this.rotatePoint(
      { x: btnRotateRect.left - this._container.offsetLeft, y: btnRotateRect.bottom - this._container.offsetTop},
      rotation,
      btnRotateRect
    );

    if (this.willButtonOverlap(topLeft, topRight, bottomRight, bottomLeft, el)) {
      button.remove();
    }
  }

  public removeButtons(el: HTMLElement): void {
    if (el) {
      el.querySelectorAll('.element-button').forEach((btn) => btn.remove());
    }
  }

  public willButtonOverlap(
    topLeft: IButtonPoint,
    topRight: IButtonPoint,
    bottomRight: IButtonPoint,
    bottomLeft: IButtonPoint,
    el?: HTMLElement
  ): boolean {
    let overlap = false;

    this._container.querySelectorAll('.overlap').forEach((div) => div.remove());

    for (const element of this.elementsInsideContainer) {
      if (element === el) continue;

      const elementRect = HTMLElementUtils.getRotatedRect(element);
      const rotation = HTMLElementUtils.getRotationDegrees(element.querySelector('.shape'));
      const elTopLeft = this.rotatePoint({ x: elementRect.left, y: elementRect.top }, rotation, elementRect);
      const elTopRight = this.rotatePoint({ x: elementRect.right, y: elementRect.top }, rotation, elementRect);
      const elBottomRight = this.rotatePoint({ x: elementRect.right, y: elementRect.bottom }, rotation, elementRect);
      const elBottomLeft = this.rotatePoint({ x: elementRect.left, y: elementRect.bottom }, rotation, elementRect);

      if (
        this.isPointInsideRect(elTopLeft, topLeft, topRight, bottomRight, bottomLeft) ||
        this.isPointInsideRect(elTopRight, topLeft, topRight, bottomRight, bottomLeft) ||
        this.isPointInsideRect(elBottomRight, topLeft, topRight, bottomRight, bottomLeft) ||
        this.isPointInsideRect(elBottomLeft, topLeft, topRight, bottomRight, bottomLeft) ||
        this.isPointInsideRect(topLeft, elTopLeft, elTopRight, elBottomRight, elBottomLeft) ||
        this.isPointInsideRect(topRight, elTopLeft, elTopRight, elBottomRight, elBottomLeft) ||
        this.isPointInsideRect(bottomRight, elTopLeft, elTopRight, elBottomRight, elBottomLeft) ||
        this.isPointInsideRect(bottomLeft, elTopLeft, elTopRight, elBottomRight, elBottomLeft)
      ) {
        overlap = true;
        break;
      }
    }

    const { left, top, right, bottom } = this._container.getBoundingClientRect();
    const checkContainerOverlap = (point: {x: number, y: number}): boolean =>
      (point.y < top || point.y > bottom || point.x < left || point.x > right);

    if (
      checkContainerOverlap(topLeft)
      || checkContainerOverlap(topRight)
      || checkContainerOverlap(bottomLeft)
      || checkContainerOverlap(bottomRight)
    ) {
      overlap = true;
    }

    return overlap;
  }

  private isPointInsideRect(
    point: IButtonPoint,
    rectLeftTop: IButtonPoint,
    rectRightTop: IButtonPoint,
    rectRightBottom: IButtonPoint,
    rectLeftBottom: IButtonPoint
  ): boolean {
    const d1 = this.sign(point, rectLeftTop, rectRightTop);
    const d2 = this.sign(point, rectRightTop, rectRightBottom);
    const d3 = this.sign(point, rectRightBottom, rectLeftBottom);
    const d4 = this.sign(point, rectLeftBottom, rectLeftTop);

    const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0) || (d4 < 0);
    const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0) || (d4 > 0);

    return !(hasNeg && hasPos);
  }

  private sign(p1: IButtonPoint, p2: IButtonPoint, p3: IButtonPoint): number {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  }

  private rotatePoint(
    point: { x: number, y: number },
    angle: number,
    center: DOMRect,
  ): { x: number, y: number } {
    const radians = angle * (Math.PI / 180);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const x = (cos * (point.x - center.x)) - (sin * (point.y - center.y)) + center.x;
    const y = (sin * (point.x - center.x)) + (cos * (point.y - center.y)) + center.y;
    return { x, y };
  }

  private getElementButton(el: HTMLElement, side: ElementButtonSide): HTMLButtonElement {
    const button = StaticDependenciesService.document.createElement('button');
    button.classList.add('element-button');
    button.style.position = 'absolute';

    const useSquareWidth = this._options.squareWidthButtons && (side === 'top' || side === 'bottom');
    const size = useSquareWidth ? el.offsetWidth : null;

    button.style.width = `${size ?? el.offsetWidth}px`;
    button.style.height = `${size ?? el.offsetHeight}px`;
    button.innerHTML = this._options.buttonInnerHTML ? this._options.buttonInnerHTML : '<i class="ri-add-line"></i>';

    if (this._data) {
      button.setAttribute('data-additional-data', this._data);
    }

    if (this._options.additionalButtonClassName) {
      button.classList.add(this._options.additionalButtonClassName);
    }

    return button;
  }

  private get elementsInsideContainer(): HTMLElement[] {
    return Array.from<HTMLElement>(this._container.querySelectorAll(`app-shape.${this._options.elementsInsideContainerSharedClassName}`));
  }
}
